import { getBillDetailPath } from '@omnea-app/fe-bills/billRoutes'
import { buildCommentHash } from '@omnea-app/fe-routing/HashProvider'
import { SupplierPageTab } from '@omnea-app/fe-suppliers/details/supplierPageTab'
import type { TFunction } from 'i18next'

export type BaseUserMention = {
  id: string
  createdAt: string | null
  comment: {
    id: string
    requestFormId: string | null
    user: {
      id: string
      firstName: string
      lastName: string
      isArchived: boolean
    } | null
    mentions: {
      id: string
      userId: string | null
      user: {
        firstName: string
        lastName: string
        email: string
        isOutOfOffice: boolean
      } | null
    }[]
    payload: { text: string }
  }
  requestId: string | null
  request: {
    id: string
    name: string
  } | null
  supplier: {
    id: string
    name: string
  } | null
  agreement: {
    id: string
    name: string
  } | null
  bill: {
    id: string
    invoiceNumber: string | null
    supplierName: string | null
  } | null
  isResolved: boolean
  userId?: string
}

type RequestMention = {
  requestId: string
  request: {
    id: string
    name: string
  }
} & Omit<BaseUserMention, 'request' | 'requestId'>

type SupplierMention = {
  supplier: {
    id: string
    name: string
  }
} & Omit<BaseUserMention, 'supplier'>

type AgreementMention = {
  agreement: {
    id: string
    name: string
  }
} & Omit<BaseUserMention, 'agreement'>

type BillMention = {
  bill: NonNullable<BaseUserMention['bill']>
} & Omit<BaseUserMention, 'bill'>

const isRequestMention = (mention: BaseUserMention): mention is RequestMention => {
  return mention.requestId !== null
}

const isSupplierMention = (mention: BaseUserMention): mention is SupplierMention => {
  return mention.supplier !== null
}

const isAgreementMention = (mention: BaseUserMention): mention is AgreementMention => {
  return mention.agreement != null
}

const isBillMention = (mention: BaseUserMention): mention is BillMention => {
  return mention.bill != null
}

const getSupplierLink = (
  supplierMention: SupplierMention,
  generateSupplierDetailRoute: (params: { supplierId: string; tab?: SupplierPageTab }) => string
) => {
  const baseUrl = document.location.origin
  const supplierId = supplierMention.supplier?.id
  const commentId = supplierMention.comment.id

  return `${baseUrl}${generateSupplierDetailRoute({ supplierId, tab: 'overview' })}#comment-${commentId}`
}

const getRequestLink = (mention: RequestMention) => {
  const requestId = mention.requestId
  const commentId = mention.comment.id
  const requestFormId = mention.comment.requestFormId

  const baseUrl = document.location.origin

  if (requestFormId) {
    return `${baseUrl}/requests/detail/${requestId}/my-tasks/${requestFormId}#comment-${commentId}`
  }
  return `${baseUrl}/requests/detail/${requestId}/overview#comment-${commentId}`
}

const getAgreementLink = (mention: AgreementMention) => {
  const baseUrl = document.location.origin
  const agreementId = mention.agreement.id
  const commentId = mention.comment.id

  return `${baseUrl}/agreements/detail/${agreementId}#comment-${commentId}`
}

const getBillLink = (mention: BillMention) => {
  const baseUrl = document.location.origin

  return `${baseUrl}${getBillDetailPath(mention.bill.id, 'conversations')}${buildCommentHash(mention.comment.id)}`
}

export const getFormCommentLink = (
  mention: BaseUserMention,
  generateSupplierDetailRoute: (params: { supplierId: string; tab?: SupplierPageTab }) => string
) => {
  const isSupplierComment = isSupplierMention(mention)
  const isRequestComment = isRequestMention(mention)

  if (isSupplierComment) {
    return getSupplierLink(mention, generateSupplierDetailRoute)
  }

  if (isRequestComment) {
    return getRequestLink(mention)
  }

  if (isAgreementMention(mention)) {
    return getAgreementLink(mention)
  }

  if (isBillMention(mention)) {
    return getBillLink(mention)
  }

  return ''
}

const getBillTitle = (mention: BillMention, t: TFunction<'dashboard'>) => {
  const { invoiceNumber, supplierName } = mention.bill

  /**
   * How is a bill named in the Mentions list? "<invoice number> · <supplier>".
   * - A missing or blank invoice number shows "No invoice number". Bills synced from an ERP can have a blank one.
   * - A missing or blank supplier shows "Unknown supplier".
   */
  return t('mentions.bill-title', {
    invoiceNumber: invoiceNumber?.trim() || t('mentions.no-invoice-number'),
    supplierName: supplierName?.trim() || t('mentions.unknown-supplier'),
  })
}

export const getMentionTitle = (mention: BaseUserMention, t: TFunction<'dashboard'>) => {
  const isSupplierComment = isSupplierMention(mention)
  const isRequestComment = isRequestMention(mention)

  if (isSupplierComment) {
    return mention.supplier?.name
  }

  if (isRequestComment) {
    return mention.request?.name
  }

  if (isAgreementMention(mention)) {
    return mention.agreement?.name
  }

  if (isBillMention(mention)) {
    return getBillTitle(mention, t)
  }

  return ''
}
